import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Expose the backend URL for the browser extension's connector content script (it reads this + the JWT
// from localStorage to auto-connect, so the candidate never pastes a token). Harmless if unused.
try { if (BACKEND_URL) localStorage.setItem("vh_backend_url", BACKEND_URL); } catch (e) { /* ignore */ }

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Company billing gate: a 402 with code "company_not_active" means the company
// hasn't paid/activated yet. Surface a clear message instead of a silent failure.
let _lastGateToast = 0;
api.interceptors.response.use(
  (res) => res,
  (error) => {
    // A Pydantic validation failure answers with detail as an ARRAY of error objects
    // ({type, loc, msg, input, url}). Over 160 call sites do toast.error(data?.detail) or render it
    // straight into JSX, and React throws "Objects are not valid as a React child" on that — so ONE
    // mistyped field anywhere took the whole screen down with a stack trace instead of showing the
    // candidate what was wrong. Flatten it to a sentence here, once, rather than at every call site.
    //
    // Only the ARRAY shape is touched. Several screens branch on an OBJECT detail
    // (detail.code, detail.plan_limit, detail.message), and stringifying those would break them.
    const data = error?.response?.data;
    if (data && Array.isArray(data.detail)) {
      data.detail = data.detail.map((x) => {
        const field = Array.isArray(x?.loc) ? x.loc.filter((p) => p !== "body").join(".") : "";
        const msg = x?.msg || "is invalid";
        return field ? `${field}: ${msg}` : msg;
      }).join("; ") || "That didn't validate — please check the form.";
    }

    const detail = error?.response?.status === 402 && error?.response?.data?.detail;
    if (detail && detail.code === "company_not_active") {
      const now = Date.now();
      if (now - _lastGateToast > 4000) {       // throttle duplicate toasts
        _lastGateToast = now;
        toast.error("Your company account isn't active yet — activate your plan to continue.");
      }
    }
    return Promise.reject(error);
  }
);

// Tell the browser extension that browser-lane work was just queued, so it drains immediately.
// The extension otherwise only notices on a 1.5-minute alarm, or on the connect it sends when this
// app LOADS — which is why Retry looked broken until the candidate refreshed the browser. The refresh
// was never the fix; it was just the only way to reach the immediate drain.
// Harmless when the extension isn't installed: nothing is listening and the message goes nowhere.
export function notifyApplyQueued() {
  try {
    window.postMessage({ source: "vetrahub-app", type: "vh-apply-queued" }, window.location.origin);
  } catch { /* never let a notification break the action that triggered it */ }
}

export default api;
